import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import Screen from '../components/Screen';
import { useFetch } from '../lib/useFetch';
import api from '../lib/api';
import { notifySuccess, notifyError } from '../lib/toast';

export default function Wallet() {
  const nav = useNavigate();
  const { data: circles } = useFetch('/me/memberships');
  const { data: stats } = useFetch('/me/stats');
  const fileRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  // You only owe a contribution in circles that are ACTIVE and that you've signed into.
  const dues = (circles || [])
    .filter(c => c.status === 'active' && c.my_status === 'signed')
    .map(c => ({ id: c.circle_id, name: c.name, amt: c.amount_per_round, due: c.next_draw }));
  const total = dues.reduce((s, d) => s + d.amt, 0);

  const onPick = (circle) => { setTarget(circle); fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file || !target) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/files/upload', fd);
      await api.post('/evidence', {
        circle_id: target.id, event_type: 'payment_recorded',
        title: `แนบสลิปโอน ${thb(target.amt)}`, meta: `${target.name} · แนบโดยคุณ`,
        payload: { amount: target.amt }, file_id: up.data.file_id,
      });
      notifySuccess(`แนบสลิปเป็นหลักฐานเรียบร้อย — เก็บใน object storage + hash ${up.data.hash_short}`);
    } catch { notifyError('เกิดข้อผิดพลาด'); } finally { setBusy(false); setTarget(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <Screen bg={PS.page}>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} data-testid="wallet-file-input" />
      <div style={{ padding: '14px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button data-testid="wallet-back-btn" onClick={() => nav('/app/me')} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0 }}><Icon.chevronL size={20} stroke={PS.ink} /></button>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>การจ่ายเงิน</div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', paddingBottom: 20 }}>
        <div className="ps-fade-up" style={{ margin: '0 16px 16px', background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 22, padding: '20px' }}>
          <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>ยอดที่ต้องจ่ายงวดนี้</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
            <div className="num" style={{ fontSize: 44, fontWeight: 500, lineHeight: 1, letterSpacing: -1.2 }}>{thb(total)}</div>
            <div style={{ fontSize: 12, color: PS.textMuted }}>รวม {dues.length} วง</div>
          </div>
          <div style={{ marginTop: 14, padding: 12, background: 'oklch(0.88 0.06 80 / 0.3)', borderRadius: 10, fontSize: 11.5, color: 'oklch(0.4 0.1 60)', lineHeight: 1.5, display: 'flex', gap: 8 }}>
            <Icon.info size={15} stroke="oklch(0.42 0.1 60)" style={{ flexShrink: 0, marginTop: 1 }} />
            โอนเงินกันเองตามจริง (PromptPay/ธนาคาร) แล้ว<b>แนบสลิปเป็นหลักฐาน</b>ในแต่ละวง — Peershare ไม่รับโอนและไม่ถือเงิน
          </div>
        </div>

        <div style={{ padding: '2px 20px 10px', fontSize: 15, fontWeight: 600 }}>งวดที่ต้องจ่าย</div>
        {dues.length === 0 && (
          <div data-testid="wallet-no-dues" style={{ margin: '0 16px 10px', padding: 18, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 16, textAlign: 'center', fontSize: 12.5, color: PS.textMuted }}>
            ตอนนี้ไม่มีงวดที่ต้องจ่าย — จะแสดงเมื่อวงที่คุณเป็นสมาชิกเริ่มเดินงวด
          </div>
        )}
        {dues.map((p) => (
          <div key={p.id} style={{ margin: '0 16px 10px', padding: 14, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, background: PS.panel, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.coin size={18} stroke={PS.ink} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
              <div style={{ fontSize: 11, color: PS.textMuted, marginTop: 2 }}>กำหนด {p.due}</div>
            </div>
            <div className="num" style={{ fontSize: 16, fontWeight: 500 }}>{thb(p.amt)}</div>
            <button data-testid={`wallet-slip-btn-${p.id}`} disabled={busy} onClick={() => onPick(p)} className="ps-press" style={{ background: PS.ink, color: PS.page, border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 11.5, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', gap: 5, alignItems: 'center' }}>
              <Icon.doc size={13} stroke={PS.page} />แนบสลิป
            </button>
          </div>
        ))}

        <div style={{ padding: '14px 20px 10px', fontSize: 15, fontWeight: 600 }}>เครดิตการจ่ายของคุณ</div>
        <div style={{ margin: '0 16px 16px', padding: 16, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 16 }}>
          <div style={{ display: 'flex', gap: 28 }}>
            <div>
              <div style={{ fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>คะแนนเครดิต</div>
              <div className="num" data-testid="wallet-credit" style={{ fontSize: 20, marginTop: 2 }}>{stats?.credit_score ?? '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>จ่ายตรงเวลา</div>
              <div className="num" data-testid="wallet-ontime" style={{ fontSize: 20, marginTop: 2, color: PS.ok }}>{stats ? `${stats.on_time_pct}%` : '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>วงที่ร่วม</div>
              <div className="num" style={{ fontSize: 20, marginTop: 2 }}>{stats?.circles_count ?? (circles?.length || 0)}</div>
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 10.5, color: PS.textMuted, lineHeight: 1.5 }}>
            คำนวณจากประวัติการแนบสลิป/จ่ายตรงเวลาจริงของคุณ — ไม่ใช่ตัวเลขสมมุติ
          </div>
        </div>
      </div>
    </Screen>
  );
}
